<style scoped lang="scss">
@import '@/assets/scss/_inputs-shared.scss';
</style>
<template>
  <div class="step3-container">
    <div class="form-container">
      
      <!-- Error si no hay datos del Step1 -->
      <div v-if="!programaFromStep1 || !yearFromStep1" class="error-message">
        <div class="alert alert-warning" role="alert">
          <strong>Error:</strong> No se encontraron los datos del programa seleccionado. 
          Por favor, regresa al Paso 1 y selecciona un programa válido.
        </div>
      </div>
      
      <!-- Sede Selector - Solo si tenemos datos del Step1 -->
      <SedeSelector 
        v-if="programaFromStep1 && yearFromStep1"
        :programa="programaFromStep1" 
        :year="yearFromStep1"
        :modalidad="modalidadFromStep1"
        :preferred-location="preferredLocation"
        :program-data="programDataFromStep1"
        :stepper-data="stepperData"
        :saved-comuna="selectedComuna"
        :saved-sede="selectedSede"
        @sede-selected="onSedeSelected"
        @sede-deselected="onSedeDeselected"
        @comuna-changed="onComunaChanged"
        @update-stepper-data="onUpdateStepperData"
      />

      <!-- Month Selector - Solo si hay comuna seleccionada -->
      <MonthSelector
        v-if="showMesInicioSection"
        :programa="programaFromStep1"
        :year="yearFromStep1"
        :modalidad="modalidadFromStep1"
        :sede-id="selectedSede"
        :program-id="programIdFromStep1"
        :selected-month="selectedMonth"
        @month-selected="onMonthSelected"
      />

      <!-- Course Selector - Solo si hay mes seleccionado -->
      <div v-if="showCursosSection">
        <!-- Section Separator -->
        <div class="section-wrapper">
          <hr class="section-separator">
        </div>
        
        <!-- Cursos Section -->
        <div class="section-wrapper section-fade-in">
          <div class="cursos-section">
            <div class="section-content">
              <CourseSelector
                :loading="loadingCourses"
                :left-column-materias="leftColumnMaterias"
                :right-column-materias="rightColumnMaterias"
                :selected-courses="selectedCourses"
                :show-debug="false"
                @update:selectedCourses="onCoursesSelected"
                @schedule-conflict="handleScheduleConflict"
                @validation-changed="onValidationChanged"
              />
            </div>
          </div>
        </div>
      </div>

      <!-- Botones de navegación  -->
      <div class="nav-btns">
        <button class="back-btn" @click="goToPreviousStep">&larr; Volver</button>
        <button 
          class="continue-btn" 
          :disabled="!canContinue" 
          @click="goToNextStep"
        >
          Continuar &rarr;
        </button>
      </div>

      <!-- Information Cards -->
      <div class="info-cards-section">
        <!-- Matemática Electiva Card -->
        <div class="info-card">
          <div class="card-header" @click="toggleCard('matematica')">
            <h3>¿Cuándo debiera seleccionar la nueva prueba <strong>Electiva de Matemáticas</strong>?</h3>
            <span class="toggle-icon" :class="{ 'expanded': expandedCards.matematica }">▼</span>
          </div>
          <div v-show="expandedCards.matematica" class="card-content">
            <p>La nueva prueba para el acceso a la Educación Superior, PAES, contempla una prueba de Matemática Electiva, también conocida como <strong>Matemática M2</strong>. Este curso debe tomarse en conjunto con <strong>Matemática Obligatoria</strong> y es especial para quienes deseen estudiar carreras ligadas a la Ciencia, Tecnología, Ingeniería o Matemática.</p>
          </div>
        </div>

        <!-- Mención vs Común Card -->
        <div class="info-card">
          <div class="card-header" @click="toggleCard('mencion')">
            <h3>¿Cuál es la diferencia entre <strong>Mención</strong> y <strong>Común</strong> en los ramos de Ciencias Naturales?</h3>
            <span class="toggle-icon" :class="{ 'expanded': expandedCards.mencion }">▼</span>
          </div>
          <div v-show="expandedCards.mencion" class="card-content">
            <p>En la prueba de Ciencias Naturales se te preguntará materia de Física, Química y Biología. Si tu eliges uno de estos ramos como tu ciencia mención, del cual se te preguntará una mayor cantidad de contenidos y que mas del 50% de las preguntas de la prueba estarán relacionadas a dicha materia. Al tomar la ciencia mención no es necesario tomar la misma ciencia en común, ya que la mención incluye la totalidad de los contenidos de dicho ramo. Los dos ramos restantes corresponden a las ciencias comunes, de los cuales tendrás que responder una menor cantidad de preguntas.</p>
            
            <div class="example-section">
              <strong>Ejemplo</strong><br>
              Biología mención + Física común + Química común
            </div>
            
            <div class="reminder-section">
              <strong>¡Recuerda!</strong><br>
              Puedes rendir la mención que tu desees, independiente de la carrera que quieras estudiar
            </div>
          </div>
        </div>

        <!-- Invierno vs Anual Card -->
        <div class="info-card">
          <div class="card-header" @click="toggleCard('invierno')">
            <h3>¿Cuál es la diferencia entre <strong>Anual</strong> e <strong>Invierno</strong>?</h3>
            <span class="toggle-icon" :class="{ 'expanded': expandedCards.invierno }">▼</span>
          </div>
          <div v-show="expandedCards.invierno" class="card-content">
            <p>El programa Anual corresponde a nuestra preparación tradicional desde Abril a Noviembre, mientras que el programa Invierno corresponde a la preparación de forma intensiva para la Paes que se rinde en Invierno, desde marzo a junio.</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Conflict Modal -->
    <ConflictModal
      :show="showConflictAlert"
      :conflict-message="conflictMessage"
      :conflict-data="conflictData"
      @close="closeConflictAlert"
      @replace-conflict="replaceConflictingSchedule"
    />

  </div>
</template>

<script>
import SedeSelector from '@/components/step3/SedeSelector.vue'
import MonthSelector from '@/components/step3/MonthSelector.vue'
import CourseSelector from '@/components/step3/CourseSelector.vue'
import ConflictModal from '@/components/step3/ConflictModal.vue'
import { coursesService, quoteService } from '@/services'
import localPageTimerMixin from '@/mixins/localPageTimerMixin'
import { formatFullCourseName } from '@/utils/subjectNameUtils'
import FloatingPriceCalculator from '@/components/pricing/FloatingPriceCalculator.vue'

export default {
  name: 'NewVersionStep3',
  mixins: [localPageTimerMixin],
  components: {
    SedeSelector,
    MonthSelector,
    CourseSelector,
    ConflictModal,
    FloatingPriceCalculator
  },
  props: {
    stepperData: {
      type: Object,
      required: true
    },
    step1Data: {
      type: Object,
      default: () => ({})
    },
    step2Data: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      programa: null,
      year: null,
      
      // === ESTADO DE LA APLICACIÓN ===
      selectedComuna: null,
      selectedComunaName: null,
      selectedSede: null,
      selectedSedeName: null,
      selectedMonth: null,
      selectedCourses: [], // Para el selector (estructura original)
      selectedCoursesSummary: [], // Para el resumen y el store
      
      // === DATOS DINÁMICOS ===
      leftColumnMaterias: [],
      rightColumnMaterias: [],
      
      // === ESTADOS DE CARGA ===
      loadingCourses: false,
      
      // === CONFLICT DETECTION ===
      showConflictAlert: false,
      conflictMessage: '',
      conflictData: null,
      
      // === VALIDATION STATE ===
      courseValidationPassed: true, // Estado de validación de cursos
      
      // === INFORMATION CARDS ===
      expandedCards: { // Control de tarjetas informativas expandidas
        // Cada tarjeta debe declararse aquí: en Vue 2 las claves agregadas
        // dinámicamente no son reactivas y el v-show no reacciona al click.
        matematica: false,
        mencion: false,
        invierno: false
      },
    }
  },
  computed: {
    // Datos del Step1
    programaFromStep1() {
      return this.step1Data?.program?.name || null;
    },
    
    yearFromStep1() {
      return this.step1Data?.year || null;
    },
    
    programIdFromStep1() {
      return this.step1Data?.program?.id || null;
    },
    
    // Datos completos del programa del Step1 (incluye ubicaciones)
    programDataFromStep1() {
      return this.step1Data?.program || null;
    },
    
    // Modalidad del Step1
    modalidadFromStep1() {
      return this.step1Data?.modalidad || null;
    },
    
    // Datos del Step2
    preferredLocation() {
      return this.step2Data?.preferredLocation || null;
    },
    
    // Validaciones de visibilidad
    showMesInicioSection() {
      // Mostrar si tenemos los datos del Step1 y una comuna y sede seleccionadas
      return this.programaFromStep1 && 
             this.yearFromStep1 && 
             this.selectedComuna && 
             this.selectedSede; // Solo si hay sede seleccionada
    },
    
    showCursosSection() {
      // Solo mostrar si tenemos todos los datos necesarios
      // Requerimos comuna, sede y mes seleccionados
      return this.programaFromStep1 && 
             this.yearFromStep1 && 
             this.selectedComuna && 
             this.selectedSede && 
             this.selectedMonth !== null;
    },
    
    canContinue() {
      // Verificar que tengamos los datos del Step1
      const hasStep1Data = this.programaFromStep1 && this.yearFromStep1;
      
      // Verificar que tengamos las selecciones requeridas
      const hasRequiredSelections = this.selectedComuna && 
             this.selectedComuna !== "" && 
             this.selectedSede && 
             this.selectedSede !== "" && 
             this.selectedMonth !== null;
      
      // Verificar que tengamos cursos seleccionados
      const hasSelectedCourses = this.selectedCoursesSummary.length > 0;
      
      // Verificar que la validación de cursos haya pasado
      const coursesValidationPassed = this.courseValidationPassed;
      
      return hasStep1Data && hasRequiredSelections && hasSelectedCourses && coursesValidationPassed;
    },
    isPdvXProgram() {
      const programaLower = this.programaFromStep1 ? this.programaFromStep1.toLowerCase() : '';
      const modalidadLower = this.modalidadFromStep1 ? this.modalidadFromStep1.toLowerCase() : '';
      
      const isPdvX = programaLower.includes('pdvx') || 
                    programaLower.includes('pdv online') ||
                    modalidadLower.includes('pdvx') || 
                    modalidadLower.includes('pdv online');
      
      return isPdvX;
    }
  },
  watch: {
    // Sincronizar con datos del stepper cuando cambien
    stepperData: {
      handler(newData) {
        if (newData && Object.keys(newData).length > 0) {
          this.syncWithStepperData(newData);
        }
      },
      immediate: true,
      deep: true
    },
    
    // Observar cambios en los datos del Step1
    step1Data: {
      handler(newData) {
        if (newData && newData.program) {
          this.programa = newData.program.name;
          this.year = newData.year;
        }
      },
      immediate: true,
      deep: true
    },
    
    // Observar cambios en los datos del Step2
    step2Data: {
      handler(newData) {
        if (newData && newData.preferredLocation && !this.isPdvXProgram) {
          this.loadPreferredLocation();
        }
      },
      immediate: true,
      deep: true
    },
    
    // Emitir cambios cuando el estado cambie
    selectedComuna() {
      this.emitStepperUpdate();
    },
    selectedSede() {
      this.emitStepperUpdate();
    },
    selectedMonth() {
      this.emitStepperUpdate();
    },
    selectedCourses: {
      handler() {
        this.emitStepperUpdate();
      },
      deep: true
    },
    selectedCoursesSummary: {
      handler() {
        this.emitStepperUpdate();
      },
      deep: true
    }
  },
  mounted() {
    // Sincronizar con datos guardados si están disponibles
    if (this.stepperData && Object.keys(this.stepperData).length > 0) {
      this.syncWithStepperData(this.stepperData);
    }
  },
  methods: {
    loadPreferredLocation() {
      // Solo cargar preferredLocation si NO es un programa PDVx
      if (this.preferredLocation && !this.isPdvXProgram) {
        // Simular la selección automática de la sede de preferencia
        this.onSedeSelected({
          comunaId: this.preferredLocation.communeId,
          comunaName: this.preferredLocation.communeName,
          sedeId: this.preferredLocation.id,
          sedeName: this.preferredLocation.name
        });
      }
    },
    
    syncWithStepperData(stepperData) {
      
      if (!stepperData || !stepperData.step3) {
        return;
      }
      
      const step3Data = stepperData.step3;
      
      // Sincronizar datos del stepper con el estado local
      if (step3Data.selectedComuna) {
        this.selectedComuna = step3Data.selectedComuna;
      }
      if (step3Data.selectedComunaName) {
        this.selectedComunaName = step3Data.selectedComunaName;
      }
      if (step3Data.selectedSede) {
        this.selectedSede = step3Data.selectedSede;
      }
      if (step3Data.selectedSedeName) {
        this.selectedSedeName = step3Data.selectedSedeName;
      }
      // Solo sincronizar el mes si realmente existe (no usar valores por defecto)
      if (step3Data.selectedMonth !== null && step3Data.selectedMonth !== undefined) {
        this.selectedMonth = step3Data.selectedMonth;
      }
      if (step3Data.selectedCourses) {
        this.selectedCourses = step3Data.selectedCourses;
      }
      if (step3Data.selectedCoursesSummary) {
        this.selectedCoursesSummary = step3Data.selectedCoursesSummary;
      }
      
    },
    
    emitStepperUpdate() {
      // Emitir datos actualizados al stepper
      this.$emit('update-data', {
        selectedComuna: this.selectedComuna,
        selectedComunaName: this.selectedComunaName,
        selectedSede: this.selectedSede,
        selectedSedeName: this.selectedSedeName,
        selectedMonth: this.selectedMonth,
        selectedCourses: this.selectedCoursesSummary, // Solo el resumen va al store
        selectedCoursesRaw: this.selectedCourses, // Para el calendario
        leftColumnMaterias: JSON.parse(JSON.stringify(this.leftColumnMaterias)), // Emitir copia profunda para evitar referencias rotas
        rightColumnMaterias: JSON.parse(JSON.stringify(this.rightColumnMaterias)),
        isFormValid: this.canContinue
      });
    },
    
    onSedeSelected(sedeData) {
      this.selectedComuna = sedeData.comunaId
      this.selectedComunaName = sedeData.comunaName
      this.selectedSede = sedeData.sedeId
      this.selectedSedeName = sedeData.sedeName
      
      // Actualizar step2.preferredLocation cuando se cambia la sede
      if (sedeData.preferredLocation) {
        this.$emit('update-step2-location', sedeData.preferredLocation);
      }
      
      // Resetear mes y cursos cuando cambia la sede
      this.selectedMonth = null;
      this.selectedCourses = [];
      this.selectedCoursesSummary = []; // Resetear el resumen
      this.leftColumnMaterias = [];
      this.rightColumnMaterias = [];
    },
    
    onSedeDeselected() {
      this.selectedSede = null;
      this.selectedSedeName = null;
      this.selectedMonth = null;
      this.selectedCourses = [];
      this.selectedCoursesSummary = []; // Resetear el resumen
      this.leftColumnMaterias = [];
      this.rightColumnMaterias = [];
    },
    
    onComunaChanged(comunaData) {
      this.selectedComuna = comunaData.comunaId;
      this.selectedComunaName = comunaData.comunaName;
      
      // Resetear sede, mes y cursos cuando cambia la comuna
      this.selectedSede = null;
      this.selectedSedeName = null;
      this.selectedMonth = null;
      this.selectedCourses = [];
      this.selectedCoursesSummary = []; // Resetear el resumen
      this.leftColumnMaterias = [];
      this.rightColumnMaterias = [];
    },
    
    onUpdateStepperData(updateData) {
      // Actualizar el stepperData con los datos de la sede virtual
      if (updateData.step === 3 && updateData.data) {
        const step3Data = updateData.data;
        
        // Actualizar el estado local
        this.selectedComuna = step3Data.selectedComuna;
        this.selectedComunaName = step3Data.selectedComunaName;
        this.selectedSede = step3Data.selectedSede;
        this.selectedSedeName = step3Data.selectedSedeName;
        
        // Emitir la actualización al stepper
        this.emitStepperUpdate();
      }
    },
    
    onMonthSelected(payload) {
      // Nuevo formato: { id, monthNumber, year, periodId } — evita confundir id de período (ej. 2) con mes calendario.
      this.selectedMonth = payload;
      
      // Resetear cursos cuando cambia el mes
      this.selectedCourses = [];
      this.selectedCoursesSummary = []; // Resetear el resumen
      this.leftColumnMaterias = [];
      this.rightColumnMaterias = [];
      
      // Cargar cursos para el mes seleccionado
      this.loadCoursesFromAPI();
    },
    
    onCoursesSelected(coursesData) {
      // Mantener el array original para el selector
      this.selectedCourses = coursesData;
      // Mapear para el resumen
      const mapped = coursesData.map(sel => {
        const allMaterias = [...this.leftColumnMaterias, ...this.rightColumnMaterias];
        let foundCourse = null;
        let foundMateria = null;
        for (const materia of allMaterias) {
          const course = materia.courses.find(c => String(c.id) === String(sel.courseId));
          if (course) {
            foundCourse = course;
            foundMateria = materia;
            break;
          }
        }
        let schedule = null;
        if (foundCourse && foundCourse.Schedules) {
          schedule = foundCourse.Schedules.find(s => String(s.id) === String(sel.scheduleId));
        }
        // Crear objeto curso con etiquetas para formatFullCourseName
        const courseWithLabels = foundCourse ? {
          ...foundCourse,
          hasMention: foundMateria?.hasMention || false,
          isElective: foundMateria?.isElective || false,
          isCommon: foundMateria?.isCommon || false
        } : null;
        
        return {
          id: sel.courseId,
          name: formatFullCourseName(foundCourse?.name || foundMateria?.subjectName || `Curso ${sel.courseId}`, courseWithLabels),
          schedule: schedule ? `${schedule.dayOfWeek} ${schedule.startTime?.slice(0,5)}-${schedule.endTime?.slice(0,5)}` : '',
        };
      });
      this.selectedCoursesSummary = mapped;
      
      // Emitir datos actualizados al stepper
      this.emitStepperUpdate();
    },
    
    onValidationChanged(canContinue) {
      this.courseValidationPassed = canContinue;
    },
    
    async loadCoursesFromAPI() {
      this.loadingCourses = true;
      try {
        // Usar los parámetros del estado actual y del Step1
        const programId = this.programIdFromStep1;
        const locationId = this.selectedSede;
        const mes = this.resolveSelectedMonthNumber();
         const data = await coursesService.loadCoursesFromAPI({
            programId,
            locationId,
            mes
        });
        if (!data) {
          this.leftColumnMaterias = [];
          this.rightColumnMaterias = [];
          return;
        }
        this.leftColumnMaterias = data.leftColumn;
        this.rightColumnMaterias = data.rightColumn;
        
      } catch (e) {
        this.leftColumnMaterias = [];
        this.rightColumnMaterias = [];
      } finally {
        this.loadingCourses = false;
      }
    },
    
    /**
     * Mes calendario 1–12 para APIs, priorizando monthNumber del período seleccionado.
     */
    resolveSelectedMonthNumber() {
      const v = this.selectedMonth;
      if (v && typeof v === 'object' && v.monthNumber != null) {
        const n = Number(v.monthNumber);
        if (n >= 1 && n <= 12) return n;
      }
      return this.convertMonthToNumber(v);
    },

    convertMonthToNumber(monthValue) {
      if (monthValue && typeof monthValue === 'object') {
        if (monthValue.monthNumber != null) {
          const n = Number(monthValue.monthNumber);
          if (n >= 1 && n <= 12) return n;
        }
        if (monthValue.id != null) {
          return this.convertMonthToNumber(String(monthValue.id));
        }
      }
      // Solo 1–12 son meses calendario; un número mayor (p.ej. id de período) no es mes.
      if (typeof monthValue === 'number') {
        if (monthValue >= 1 && monthValue <= 12) return monthValue;
        console.warn('Valor numérico no es un mes 1–12:', monthValue);
        return 4;
      }
      
      // Si es un string, intentar convertirlo
      if (typeof monthValue === 'string') {
        // Mapeo de nombres de meses a números
        const monthMap = {
          'enero': 1, 'febrero': 2, 'marzo': 3, 'abril': 4,
          'mayo': 5, 'junio': 6, 'julio': 7, 'agosto': 8,
          'septiembre': 9, 'octubre': 10, 'noviembre': 11, 'diciembre': 12
        };
        
        // Buscar el nombre del mes en el string
        const lowerMonth = monthValue.toLowerCase();
        for (const [monthName, monthNumber] of Object.entries(monthMap)) {
          if (lowerMonth.includes(monthName)) {
            return monthNumber;
          }
        }
        
        // Si no se encuentra, intentar extraer solo el número
        const numericMatch = monthValue.match(/\d+/);
        if (numericMatch) {
          const num = parseInt(numericMatch[0]);
          if (num >= 1 && num <= 12) {
            return num;
          }
        }
      }
      
      // Valor por defecto si no se puede convertir
      console.warn('No se pudo convertir el mes:', monthValue, 'usando valor por defecto: 4');
      return 4;
    },
    
    handleScheduleConflict(conflict) {
      this.conflictMessage = `El horario "${conflict.newCourse.name}" (${this.formatTime(conflict.newSchedule.startTime)}-${this.formatTime(conflict.newSchedule.endTime)} ${conflict.newSchedule.dayOfWeek}) se superpone con "${conflict.existingCourse.name}" (${this.formatTime(conflict.existingSchedule.startTime)}-${this.formatTime(conflict.existingSchedule.endTime)} ${conflict.existingSchedule.dayOfWeek}).`;
      this.conflictData = conflict;
      this.showConflictAlert = true;
    },
    
    closeConflictAlert() {
      this.showConflictAlert = false;
      this.conflictMessage = '';
      this.conflictData = null;
    },
    
    replaceConflictingSchedule(payload) {
      // Permitir recibir conflictData por parámetro o usar el almacenado
      const data = payload || this.conflictData;
      if (!data || !data.newCourse || !data.newSchedule || !data.existingCourse) {
        this.closeConflictAlert();
        return;
      }
      
      const existingCourseId = String(data.existingCourse.id);
      const newCourseId = String(data.newCourse.id);
      const newScheduleId = String(data.newSchedule.id);
      
      // 1) Remover selecciones del curso en conflicto
      this.selectedCourses = this.selectedCourses.filter(sel => String(sel.courseId) !== existingCourseId);
      
      // 2) Agregar la nueva selección
      this.selectedCourses.push({ courseId: newCourseId, scheduleId: newScheduleId });
      
      // 3) Recalcular el resumen y emitir cambios al stepper
      this.onCoursesSelected([...this.selectedCourses]);
      
      // 4) Cerrar modal
      this.closeConflictAlert();
    },
    
    formatTime(timeString) {
      if (!timeString) return '';
      return timeString.slice(0, 5); // Extrae HH:MM de HH:MM:SS
    },
    
    async goToNextStep() {
      if (!this.canContinue) return;
      try {
        // Obtener los datos necesarios
        const locationId = this.selectedSede;
        const programId = this.programIdFromStep1;
        let coursesIds = [
          ...new Set(this.selectedCourses.map(c => String(c.courseId)).filter(Boolean))
        ].join(',');
        const studentId = this.step2Data?.studentId;
        const orderId = this.step2Data?.orderId;
        const missing = [];
        if (!locationId) missing.push('Sede');
        if (!programId) missing.push('Programa');
        if (!studentId) missing.push('Estudiante');
        if (!orderId) missing.push('Orden');
        // Si no hay cursos seleccionados, obtener el primer id de la API
        if (!coursesIds) {
          const mes = this.resolveSelectedMonthNumber();
          const cursos = await coursesService.getCoursesRaw({ programId, locationId, mes });
          if (cursos && Array.isArray(cursos)) {
            const firstValidCourse = cursos.find(c => c && c.id);
            if (firstValidCourse) {
              coursesIds = firstValidCourse.id.toString();
            } else {
              missing.push('Cursos');
            }
          } else {
            missing.push('Cursos');
          }
        }
        if (!coursesIds) missing.push('Cursos');
        if (missing.length > 0) {
          const message = `No se puede cotizar porque faltan los siguientes datos:\n\n- ${missing.join('\n- ')}`;
          if (window.Swal) {
            window.Swal.fire({
              icon: 'warning',
              title: 'Datos incompletos',
              html: `<p>Por favor completa los siguientes campos antes de continuar:</p><ul style='text-align:left;'>${missing.map(m => `<li>${m}</li>`).join('')}</ul>`,
              confirmButtonText: 'Entendido'
            });
          } else {
            alert(message);
          }
          return;
        }
        const quote = await quoteService.getQuote({ locationId, programId, coursesIds, studentId, orderId });
        this.$emit('update-data', { quote });
        this.$emit('next-step');
      } catch (error) {
        alert('Ocurrió un error al cotizar: ' + (error?.response?.data?.message || error.message));
      }
    },
    
    goToPreviousStep() {
      this.$emit('previous-step');
    },

    /**
     * Maneja el evento cuando un cupón de descuento se aplica
     */
    
    // Control de tarjetas informativas
    toggleCard(cardType) {
      // $set garantiza reactividad incluso si la clave no está declarada en data
      this.$set(this.expandedCards, cardType, !this.expandedCards[cardType]);
    },
    
  }
}
</script>

<style scoped>
.step3-container {
  padding: 20px;
}

.form-container {
  max-width: 936px;
  margin: 0 auto;
}

.section-wrapper {
  margin-bottom: 24px;
}

.section-separator {
  border: none;
  height: 1px;
  background-color: #e0e0e0;
  margin: 24px 0;
}

.section-fade-in {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.cursos-section {
  background: white;
  border-radius: 8px;
  padding: 24px;
}

.section-content {
  width: 100%;
}

.nav-btns {
  display: flex;
  justify-content: center;
  gap: 18px;
  margin: 8px 0 8px 0;
}

.back-btn, .continue-btn {
  border: none;
  border-radius: 24px;
  padding: 10px 26px;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  background: #f5f5f5;
  color: #888;
  transition: background 0.2s, color 0.2s;
}

.back-btn:hover {
  background: #e0e0e0;
  color: #666;
}

/* Botón continuar habilitado: fondo negro, texto blanco */
.continue-btn:enabled {
  background: #111;
  color: #fff;
}

.continue-btn:enabled:hover {
  background: #333;
}

.continue-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  background: #111;
  color: #fff;
}

/* Responsive Design */
@media (max-width: 768px) {
  .nav-btns {
    flex-direction: column;
    gap: 12px;
    margin: 20px 0 15px 0;
  }
  
  .back-btn, .continue-btn {
    width: 100%;
    padding: 12px 26px;
  }
  
  /* Cambiar orden en mobile: Continuar arriba, Volver abajo */
  .continue-btn {
    order: -1;
  }
  
  .back-btn {
    order: 1;
  }
}

/* Information Cards Styles */
.info-cards-section {
  margin-bottom: 20px;
  padding: 24px;
  margin-top: 20px;
}

.info-card {
  background: #fff3cd;
  border: 1px solid #ffeaa7;
  border-radius: 8px;
  margin-bottom: 15px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.card-header {
  padding: 15px 20px;
  cursor: pointer;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #fff3cd;
  border-bottom: 1px solid #ffeaa7;
  transition: background-color 0.2s ease;
}

.card-header:hover {
  background: #ffeaa7;
}

.card-header h3 {
  margin: 0;
  font-size: 16px;
  font-weight: 500;
  color: #856404;
  line-height: 1.4;
  flex: 1;
}

.card-header h3 strong {
  font-weight: 700;
  color: #6f5400;
}

.toggle-icon {
  font-size: 14px;
  color: #856404;
  transition: transform 0.2s ease;
  margin-left: 15px;
  flex-shrink: 0;
}

.toggle-icon.expanded {
  transform: rotate(180deg);
}

.card-content {
  padding: 20px;
  background: white;
  color: #495057;
  line-height: 1.6;
}

.card-content p {
  margin: 0 0 15px 0;
  font-size: 14px;
}

.card-content p:last-child {
  margin-bottom: 0;
}

.example-section {
  background: #f8f9fa;
  padding: 12px 16px;
  border-left: 4px solid #252525;
  margin: 15px 0;
  border-radius: 4px;
  font-size: 14px;
}

.reminder-section {
  background: #e7f3ff;
  padding: 12px 16px;
  border-left: 4px solid #252525;
  margin: 15px 0;
  border-radius: 4px;
  font-size: 14px;
}

/* Responsive adjustments for info cards */
@media (max-width: 768px) {
  .info-cards-section {
    margin-top: 20px;
  }
  
  .card-header {
    padding: 12px 15px;
  }
  
  .card-header h3 {
    font-size: 14px;
  }
  
  .card-content {
    padding: 15px;
  }
  
  .card-content p {
    font-size: 13px;
  }
  
  .example-section,
  .reminder-section {
    padding: 10px 12px;
    font-size: 13px;
  }
}

@media (max-width: 480px) {
  .card-header {
    padding: 10px 12px;
  }
  
  .card-header h3 {
    font-size: 13px;
  }
  
  .toggle-icon {
    margin-left: 10px;
  }
  
  .card-content {
    padding: 12px;
  }
  
  .card-content p {
    font-size: 12px;
  }
  
  .example-section,
  .reminder-section {
    padding: 8px 10px;
    font-size: 12px;
  }
}
</style>
